import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Custom hook for responsive design breakpoints
 * Returns boolean flags for current screen size
 */
export const useResponsive = () => {
  const theme = useTheme();
  
  // Breakpoint definitions
  // xs: 0-599px (mobile)
  // sm: 600-899px (tablet)
  // md: 900-1199px (small desktop)
  // lg: 1200-1535px (desktop)
  // xl: 1536px+ (large desktop)
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899px
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 900px
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  
  // Specific breakpoints for fine control
  const isSmallMobile = useMediaQuery('(max-width:375px)'); // Small phones
  const isMediumMobile = useMediaQuery('(min-width:376px) and (max-width:599px)'); // Regular phones
  const isSmallTablet = useMediaQuery('(min-width:600px) and (max-width:767px)'); // Small tablets
  const isLargeTablet = useMediaQuery('(min-width:768px) and (max-width:899px)'); // Large tablets/small laptops
  
  // Orientation
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isPortrait = useMediaQuery('(orientation: portrait)');
  
  // Touch device detection
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
  
  // Helper function to get current breakpoint name
  const getBreakpoint = () => {
    if (isMobile) return 'mobile';
    if (isTablet) return 'tablet';
    if (isDesktop && !isLargeDesktop) return 'desktop';
    if (isLargeDesktop) return 'largeDesktop';
    return 'unknown';
  };
  
  // Helper function to check if we should show mobile layout
  // This includes tablets in portrait mode for better UX
  const shouldShowMobileLayout = isMobile || (isTablet && isPortrait);
  
  // Helper function to check if we should show drawer-style sidebar
  const shouldShowDrawerSidebar = isMobile || isTablet;
  
  return {
    // Main breakpoints
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    
    // Detailed breakpoints
    isSmallMobile,
    isMediumMobile,
    isSmallTablet,
    isLargeTablet,
    
    // Orientation
    isLandscape,
    isPortrait,
    
    // Device type
    isTouchDevice,
    
    // Helper functions
    getBreakpoint,
    shouldShowMobileLayout,
    shouldShowDrawerSidebar,
    
    // Raw theme for custom queries
    theme
  };
};

export default useResponsive;